import { OptionProps } from '@mui/base';
import { Autocomplete, Checkbox, InputLabel, TextField, Typography } from '@mui/material';
import { DesktopDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { EditExternalUserForm } from 'components/Drawer/types';
import dayjs from 'dayjs';
import { Role } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { upperFirst } from 'lodash';
import React from 'react';
import { Control, Controller, UseFormRegister } from 'react-hook-form';
import styled from 'styled-components';
import AlertStatus from 'ui/atoms/AlertStatus/AlertStatus';
import AutocompletePaper from 'ui/atoms/AutocompletePaper/AutocompletePaper';
import Icon from 'ui/atoms/Icon/Icon';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

const IconWrapper = styled.div`
  cursor: pointer;
  align-items: center;
  height: 24px;
`;

export interface Props {
  register: UseFormRegister<EditExternalUserForm>;
  nameInputValue: string | undefined;
  handleNameInputOnChange: (value: string) => void;
  nameInputValidation: string | undefined;
  isUserAddedViaKeySharing: boolean;
  emailInputValue: string | undefined | null;
  status: boolean | undefined;
  showValidation: boolean;
  handleOnResendInvitationClick?: () => void;
  switchIsAdministratorRoleEnabled: boolean | undefined;
  handleSwitchIsAdministratorRoleEnabledOnChange: (() => void) | undefined;
  switchIsExpirationDateEnabled: boolean | undefined;
  handleSwitchIsExpirationDateOnChange: () => void;
  inputExpirationDateToValue: dayjs.Dayjs | null;
  handleInputExpirationDateToValue: (value: dayjs.Dayjs | null) => void;
  inputExpirationDateToValueValidation: string | undefined;
  control: Control<EditExternalUserForm, object>;
  roleSelectValue: OptionProps<Role | null>;
  roleSelectOption: OptionProps<Role | null>[];
  handleRoleSelectChange: (value: OptionProps<Role> | null) => void;
  groupsSelectValue: null | OptionProps<string | null>;
  groupsSelectOption: OptionProps<string | null>[];
  handleGroupsSelectChange: (value: OptionProps<string> | null) => void;
  roleInputValidation: string | undefined;
  isAdminEditingHimself: boolean;
  isMultiAdminEnabled: boolean;
}

const EditExternalUser: React.FC<React.PropsWithChildren<Props>> = ({
  register,
  nameInputValue,
  emailInputValue,
  handleNameInputOnChange,
  nameInputValidation,
  isUserAddedViaKeySharing = false,
  status,
  showValidation,
  handleOnResendInvitationClick,
  handleSwitchIsAdministratorRoleEnabledOnChange,
  switchIsAdministratorRoleEnabled,
  switchIsExpirationDateEnabled,
  handleSwitchIsExpirationDateOnChange,
  inputExpirationDateToValue,
  inputExpirationDateToValueValidation,
  handleInputExpirationDateToValue,
  control,
  roleSelectValue,
  roleSelectOption,
  handleRoleSelectChange,
  groupsSelectValue,
  groupsSelectOption,
  handleGroupsSelectChange,
  roleInputValidation,
  isAdminEditingHimself,
  isMultiAdminEnabled
}) => {
  const { translation, getLanguage, getLocaleForDayjs } = useTranslation();

  return (
    <ComponentWrapper flexDirection="column" gap="1.5rem" margin="0.5rem 0 0 0">
      <TextField
        {...register('name')}
        onChange={(event) => handleNameInputOnChange(event.target.value)}
        variant="outlined"
        InputLabelProps={{ shrink: !!nameInputValue }}
        size="medium"
        label={translation.name}
        value={nameInputValue}
        id="drawer-input-name"
        error={showValidation && nameInputValidation !== undefined}
        helperText={showValidation ? nameInputValidation : undefined}
      />
      <TextField
        {...register('email')}
        variant="outlined"
        InputLabelProps={{ shrink: !!emailInputValue }}
        size="medium"
        label={translation.e_mail}
        value={emailInputValue}
        id="drawer-input-email"
        disabled
        helperText={isUserAddedViaKeySharing ? translation.this_user_is_invited_via_key_sharing : undefined}
      />
      <Controller
        control={control}
        name="group"
        render={() => (
          <Autocomplete
            {...register('group')}
            options={groupsSelectOption}
            value={groupsSelectValue}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, value) => {
              handleGroupsSelectChange(value as OptionProps<string>);
            }}
            getOptionLabel={(option) => option.label as string}
            id="drawer-input-group"
            PaperComponent={AutocompletePaper}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translation.external_groups}
                id="drawer-select-group-input-id"
                variant="outlined"
                size="medium"
                InputLabelProps={{ shrink: !!groupsSelectValue?.value }}
              />
            )}
            sx={{
              '& .MuiSvgIcon-root': {
                fill: 'white',
                fillOpacity: 0.56
              }
            }}
          />
        )}
      />
      <ComponentWrapper justifyContent="space-between" alignItems="center" gap="8px">
        <Controller
          control={control}
          name="role"
          render={() => (
            <Autocomplete
              {...register('role')}
              options={roleSelectOption}
              value={roleSelectValue}
              onChange={(_, value) => {
                handleRoleSelectChange(value as OptionProps<Role>);
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={(option) => option.label as string}
              id="drawer-input-role"
              clearOnBlur={false}
              disableClearable
              PaperComponent={AutocompletePaper}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translation.role}
                  id="drawer-select-role-input-id"
                  variant="outlined"
                  size="medium"
                  fullWidth
                  InputLabelProps={{ shrink: !!roleSelectValue.value }}
                  error={showValidation && roleInputValidation !== undefined}
                  helperText={showValidation ? roleInputValidation : undefined}
                />
              )}
              sx={{
                '& .MuiSvgIcon-root': {
                  fill: 'white',
                  fillOpacity: 0.56
                }
              }}
            />
          )}
        />
        <Tooltip
          elementOnHover={
            <ComponentWrapper flexDirection="column" width="20rem" padding="1rem">
              <Typography variant="body1">
                {
                  translation.a_user_with_the_owner_role_has_the_ability_to_invite_other_users_to_this_group_up_to_maximum_user_count
                }
                .
              </Typography>
            </ComponentWrapper>
          }
          id="role-tooltip"
        >
          <IconWrapper>
            <Icon name="Info" id="locks-i-icon-role" />
          </IconWrapper>
        </Tooltip>
      </ComponentWrapper>
      {isMultiAdminEnabled && (
        <ComponentWrapper alignItems="center">
          <Checkbox
            {...register('isAdministratorRole')}
            color="primary"
            id="drawer-input-isAdministratorRole-checkbox"
            checked={switchIsAdministratorRoleEnabled}
            disabled={!emailInputValue || isAdminEditingHimself}
            onClick={handleSwitchIsAdministratorRoleEnabledOnChange}
          />
          <Typography>{translation.administrator}</Typography>
        </ComponentWrapper>
      )}
      {!isAdminEditingHimself && (
        <ComponentWrapper flexDirection="column" gap="1.5rem">
          <ComponentWrapper alignItems="center">
            <Checkbox
              {...register('isOfficeModeEnabled')}
              color="primary"
              id="drawer-input-expiration-date-checkbox"
              checked={Boolean(switchIsExpirationDateEnabled)}
              disabled={!!switchIsAdministratorRoleEnabled}
              onClick={handleSwitchIsExpirationDateOnChange}
            />
            <Typography>{translation.enable_expiration_date}</Typography>
          </ComponentWrapper>
          <Controller
            control={control}
            name="expirationDate"
            render={() => (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getLocaleForDayjs(getLanguage).name}>
                <DesktopDateTimePicker
                  label={upperFirst(translation.expiration_date)}
                  value={inputExpirationDateToValue}
                  views={['year', 'day', 'hours', 'minutes']}
                  onChange={handleInputExpirationDateToValue}
                  disabled={!switchIsExpirationDateEnabled || switchIsAdministratorRoleEnabled}
                  slotProps={{
                    textField: {
                      helperText:
                        showValidation && !!inputExpirationDateToValueValidation
                          ? inputExpirationDateToValueValidation
                          : undefined,
                      id: 'drawer-input-expiration-date'
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </ComponentWrapper>
      )}
      <ComponentWrapper flexDirection="column">
        <InputLabel variant="outlined" shrink>
          {translation.atlas_access_app}
        </InputLabel>
        <AlertStatus
          id="atlas-access-app-status"
          isConnected={!!status}
          buttonText={translation.resend_invitation}
          onClick={
            !isUserAddedViaKeySharing && emailInputValue !== undefined ? handleOnResendInvitationClick : undefined
          }
        />
      </ComponentWrapper>
    </ComponentWrapper>
  );
};

export default EditExternalUser;
